import axiosInstance from '../utils/axiosConfig';

/**
 * Statement of Accounts (SOA) service.
 *
 * Wraps three backend endpoints:
 *   - GET  /api/crm/profiles/{id}/financial-movements/
 *   - GET  /api/crm/profiles/{id}/deliveries-with-financial-activity/
 *   - POST /api/invoicing/statement-of-accounts/generate/  (returns PDF blob)
 *
 * The SOA PDF endpoint is approver-gated server-side; the frontend should
 * additionally hide entry-points for non-approvers, but never assume the
 * UI gate is sufficient.
 */

const omitEmpty = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        out[key] = value;
    });
    return out;
};

export const getPartnerMovements = async (profileId, params = {}) => {
    const response = await axiosInstance.get(
        `/api/crm/profiles/${profileId}/financial-movements/`,
        { params: omitEmpty(params) },
    );
    return response.data;
};

export const getPartnerDeliveriesWithActivity = async (profileId) => {
    const response = await axiosInstance.get(
        `/api/crm/profiles/${profileId}/deliveries-with-financial-activity/`,
    );
    return response.data;
};

/**
 * Read-only commissions-owed roll-up for a partner.
 *
 * Returns per-currency pending/confirmed subtotals plus the underlying
 * commission line items (Commission Sales / Commission Purchase). No model
 * writes, no FX conversion.
 *
 *   - GET /api/crm/profiles/{id}/commissions-owed/
 */
export const getPartnerCommissionsOwed = async (profileId) => {
    const response = await axiosInstance.get(
        `/api/crm/profiles/${profileId}/commissions-owed/`,
    );
    return response.data;
};

/**
 * Generate and return a Statement of Accounts PDF as a Blob.
 *
 * Always uses POST so the (potentially long) filter payload does not end
 * up in URL/query logs alongside partner data.
 *
 * @param {Object} payload
 * @param {number} payload.partner_id   required
 * @param {string} [payload.date_from]  ISO YYYY-MM-DD
 * @param {string} [payload.date_to]    ISO YYYY-MM-DD
 * @param {number} [payload.delivery_id]
 * @param {number} [payload.hms_entity_id]
 * @returns {Promise<Blob>}
 */
export const downloadStatementOfAccounts = async (payload) => {
    const body = omitEmpty(payload);
    if (!body.partner_id) {
        throw new Error('partner_id is required to generate a Statement of Accounts.');
    }
    const response = await axiosInstance.post(
        '/api/invoicing/statement-of-accounts/generate/',
        body,
        { responseType: 'blob' },
    );
    return response.data;
};

const statementService = {
    getPartnerMovements,
    getPartnerDeliveriesWithActivity,
    getPartnerCommissionsOwed,
    downloadStatementOfAccounts,
};

export default statementService;
